import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useAppStore } from "../store/useAppStore";
import { TODAY } from "../domain/fixtures";
import { ContextHeader, type Readout } from "../components/layout/ContextHeader";
import { Chip } from "../components/ui/Chip";
import { StateTrack } from "../components/ui/StateTrack";
import { SituationPanel } from "../components/situations/SituationPanel";

function daysBetween(a: string, b: string) {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86_400_000);
}

export function UnitDetail() {
  const { unitId } = useParams();
  const unit = useAppStore((s) => s.units.find((u) => u.id === unitId));
  const site = useAppStore((s) => s.sites.find((si) => si.id === unit?.siteId));
  const tenant = useAppStore((s) => s.tenants.find((t) => t.id === unit?.tenantId));
  const lease = useAppStore((s) => s.leases.find((l) => l.unitId === unitId));
  const receivable = useAppStore((s) => s.receivables.find((r) => r.leaseId === lease?.id));
  const workOrders = useAppStore((s) => s.workOrders.filter((w) => w.unitId === unitId && w.status !== "closed"));
  const coi = useAppStore((s) => s.cois.find((c) => c.unitId === unitId));
  const messages = useAppStore((s) => s.messages.filter((m) => m.unitId === unitId));
  const situations = useAppStore((s) => s.situations.filter((sit) => sit.unitId === unitId));
  const today = TODAY;

  const readouts: Readout[] = useMemo(() => {
    const out: Readout[] = [];
    if (receivable) out.push({ key: "AR", value: `$${receivable.balanceDue.toLocaleString()}`, tone: receivable.daysLate > 0 ? "bad" : "default" });
    if (receivable) out.push({ key: "LATE", value: `${receivable.daysLate}D`, tone: receivable.daysLate > 5 ? "bad" : "default" });
    if (lease) out.push({ key: "RENEWAL", value: `${daysBetween(today, lease.renewalWindowOpensDate)}D`, tone: "warn" });
    out.push({ key: "WORK", value: String(workOrders.length) });
    if (coi) out.push({ key: "COI", value: coi.status === "verified" ? "OK" : coi.status.toUpperCase() });
    return out;
  }, [receivable, lease, workOrders.length, coi, today]);

  if (!unit) {
    return <p className="p-6 text-[13.5px] text-muted-foreground">Unit not found.</p>;
  }

  return (
    <div className="flex flex-1 flex-col">
      <ContextHeader
        onBack
        crumb={`${site?.name.toUpperCase()} › ${unit.buildingLabel.toUpperCase()}`}
        title={unit.unitNumber}
        subtitle={tenant ? `${tenant.companyName} · ${unit.kind} · ${unit.sqft.toLocaleString()} sq ft` : `Vacant · ${unit.kind} · ${unit.sqft.toLocaleString()} sq ft`}
        readouts={readouts}
      />

      <div className="flex flex-1 flex-col gap-4 p-4 lg:flex-row lg:gap-6">
        <div className="flex flex-1 flex-col gap-4 lg:max-w-md">
          {lease && (
            <Panel title="Lease">
              <Kv label="Monthly rent" value={`$${lease.monthlyRent.toLocaleString()}`} />
              <Kv label="Lease ends" value={lease.endDate} />
              <Kv label="Renewal opens" value={lease.renewalWindowOpensDate} />
              {tenant?.tenantSince && <Kv label="Tenant since" value={tenant.tenantSince} />}
            </Panel>
          )}

          {tenant && (
            <Panel title="Contact">
              <Kv label="Contact" value={tenant.contactName} />
              <Kv label="Email" value={tenant.contactEmail} />
              <Kv label="Phone" value={tenant.contactPhone} />
            </Panel>
          )}

          {workOrders.length > 0 && (
            <Panel title="Open work">
              <ul className="flex flex-col gap-2">
                {workOrders.map((wo) => (
                  <li key={wo.id} className="rounded-sm border border-border p-2.5">
                    <p className="font-action text-[13.5px] font-bold text-foreground">{wo.title}</p>
                    <p className="mt-0.5 font-mono text-[10.5px] uppercase text-muted-foreground">{wo.status.replace(/-/g, " ")}</p>
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          <Panel title="Access · services">
            <Kv label="Door access" value="Active · 2 fobs" />
            {coi && <Kv label="COI" value={coi.status === "verified" ? `Valid → ${coi.expiresOn}` : coi.status} />}
          </Panel>

          {messages.length > 0 && (
            <Panel title="Recent communication">
              <ul className="flex flex-col gap-2">
                {messages
                  .slice()
                  .reverse()
                  .map((m) => (
                    <li key={m.id} className="text-[12.5px] text-foreground">
                      <span className="font-mono text-[10px] uppercase text-muted-foreground">{m.channel}</span> — {m.body}
                    </li>
                  ))}
              </ul>
            </Panel>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-3">
          <h2 className="font-action text-[12px] font-bold uppercase tracking-wide text-muted-foreground">Associated situations</h2>
          {situations.length === 0 && <p className="text-[13px] text-muted-foreground">No open situations for this unit.</p>}
          {situations.map((s) => (
            <div key={s.id} className="rounded-md border border-border bg-card">
              <div className="flex items-center justify-between px-3.5 py-2.5">
                <Link to={`/situations/${s.id}`} className="font-action text-[14px] font-bold text-primary hover:underline">
                  {s.title}
                </Link>
                <Chip tone={s.status === "open" ? "attention" : "success"}>{s.status}</Chip>
              </div>
              {s.workflow && (
                <div className="px-3.5 pb-3">
                  <StateTrack steps={s.workflow.steps} compact />
                </div>
              )}
              {s.id === "sit-hartley" && (
                <div className="border-t border-border">
                  <SituationPanel situation={s} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-md border border-border bg-card">
      <h3 className="border-b border-border px-3.5 py-2 font-action text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{title}</h3>
      <div className="flex flex-col gap-2 p-3.5">{children}</div>
    </section>
  );
}

function Kv({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[12.5px] text-muted-foreground">{label}</span>
      <span className="font-mono text-[13px] font-semibold text-foreground">{value}</span>
    </div>
  );
}
